import { useState } from "react"
import { useTranslation } from "react-i18next"

import { formatPrice } from "@/components/brand/settings/products/format-price"
import type { ShopifyPreviewItem } from "@/types/integrations"

/**
 * OKS-960 — One product in the pre-commit sample (UX §2 S1, step 3).
 *
 * ⚠️ `ProductThumbnail` is deliberately NOT reused here. It is a fixed 48×48
 * recognition thumbnail for a list the brand is scanning; this card has one
 * job — letting the brand answer "is this MY store?" — and a 48px image cannot
 * do it. Different question, different component.
 *
 * The `?width=` merge stays in the component rather than the database, the same
 * rule `ProductThumbnail` documents: persisting a derived CDN parameter freezes
 * it into every row and needs a backfill when it changes.
 *
 * Custom component under `components/brand/**`, NOT under `components/ui/**` —
 * it is a feature card, not a primitive, so it needs no exemption-ledger entry.
 */
const PREVIEW_IMAGE_WIDTH = 400

function previewImageUrl(raw: string): string {
  try {
    const url = new URL(raw)
    url.searchParams.set("width", String(PREVIEW_IMAGE_WIDTH))
    return url.toString()
  } catch {
    // Relative or malformed: hotlink unchanged rather than dropping the image.
    // A big image beats no image.
    return raw
  }
}

export default function ShopifyPreviewCard({
  item,
  currency,
  language,
}: {
  item: ShopifyPreviewItem
  /** The currency the brand has selected at step 3 — changing it re-formats
   *  these prices immediately, client-side (AC3). */
  currency: string
  language: string
}) {
  const { t } = useTranslation()
  const [failed, setFailed] = useState(false)

  const price = formatPrice(item.price_cents, currency, language)

  return (
    <div className="min-w-0" data-testid="brand-shopify-preview-card">
      {item.image_url === null || failed ? (
        /* Labelled rather than given an empty alt: the absence of an image is
           information (this product has no image), not decoration (UX §6). */
        <div
          className="bg-muted aspect-square w-full rounded"
          role="img"
          aria-label={t("settings.brand.products.image_missing")}
          data-testid="brand-shopify-preview-card-placeholder"
        />
      ) : (
        /* Explicit width/height + object-cover: without them six hotlinked
           images reflow the grid as they arrive, and step 3 is the one screen
           where the brand is reading carefully. */
        <img
          src={previewImageUrl(item.image_url)}
          alt={item.title}
          width={PREVIEW_IMAGE_WIDTH}
          height={PREVIEW_IMAGE_WIDTH}
          loading="lazy"
          className="aspect-square w-full rounded object-cover"
          onError={() => setFailed(true)}
        />
      )}

      {/* line-clamp-2 + min-w-0: product titles are unbounded and a long one
          must not push the grid wide at 375px. The full value stays reachable
          on hover/AT via `title`. */}
      <p className="mt-2 line-clamp-2 text-sm" title={item.title}>
        {item.title}
      </p>

      {/* Own line, tabular-nums — a price that shifts its own width as the
          currency changes reads as a glitch. */}
      {price !== null ? (
        <p className="text-muted-foreground text-sm tabular-nums">{price}</p>
      ) : null}
    </div>
  )
}
